/**
 * Peticiones de la sección Ecommerce a cms-api (módulo `ecommerce`, contrato en
 * doc-ia/feat-ecommerce-section/01-documentos/contrato-api.md, sección 4).
 *
 * Nombres como en cms-api: `oid` es el programa y `ofr`, la oferta.
 *
 * Cada función devuelve una promesa que **nunca se rechaza**:
 *   { ok: true, data }               data = data.data[<método>] de la respuesta
 *   { ok: false, error }             error = { code, msg, type, shown }
 *
 * - `type`: el `type_msg` de cms-api (`exception`, `standard`, `for_user`), o
 *   `network` si no hubo respuesta y `server` si cms-api respondió con fallo
 *   (HTTP 500 u `ok: false`).
 * - `shown`: true si el usuario ya ha visto el error (el aviso general de
 *   `codeInterpreter` para los `standard`, o la sesión caducada, que recarga la
 *   página). La pantalla solo pinta los que llegan con `shown: false`.
 *
 * A diferencia de `HandleRequest`, los fallos de red y de servidor no abren el
 * aviso general: los pinta la pantalla en su sitio (el estado de error de la
 * tabla con «Reintentar», el campo de la URL…), para no enseñar dos avisos.
 */
import "regenerator-runtime/runtime.js";
import ApiService from './../../api_service';
import codeInterpreter from './../request_map';
import { ecommerce as url } from './../../urls';
import { c_query, c_ok, c_error } from 'utils/console';

const OK = 'ok';
const EXCEPTION = 'exception';
const SESSION_CODES = ['invs', 'exps'];

const failure = (code, msg, type, shown) => ({ ok: false, error: { code, msg, type, shown } });

// Quita los parámetros vacíos: cms-api aplica sus valores por defecto
const compact = (params = {}) => Object.keys(params).reduce((acc, key) => {
  const value = params[key];
  if (value !== undefined && value !== null && value !== '') acc[key] = value;
  return acc;
}, {});

const post = async (method, params = {}) => {
  const api = url[method];
  const query = { api, data: params };
  c_query('REQUEST:', query);
  try {
    const { data } = await ApiService.post(api, params);

    if (data && data.ok && data.data && data.data.result === OK) {
      c_ok('SUCCESS:', data);
      return { ok: true, data: data.data[method] };
    }

    if (data && data.ok && data.data) {
      c_error('ERROR:', data);
      const code = data.data.result;
      // codeInterpreter muestra los standard y recarga si la sesión caducó
      const interpreted = codeInterpreter(data, query);
      const type = data.data.type_msg || 'standard';
      const shown = SESSION_CODES.includes(code) || type !== EXCEPTION;
      return failure(code, interpreted ? interpreted.msg : undefined, type, shown);
    }

    c_error('ERROR:', data);
    return failure(null, undefined, 'server', false);
  } catch (e) {
    c_error('ERROR CATCH:', e);
    const type = e && e.response ? 'server' : 'network';
    return failure(null, undefined, type, false);
  }
};

/**
 * Listado de programas, en el formato de tabla (`tt`, `he`, `pa`, `ros`).
 * @param {{h?: string, sts?: string, ty?: string, rpp?: number, cp?: number, sk?: string, sd?: string}} params
 */
export const getPrograms = (params = {}) => post('getPrograms', compact(params));

/**
 * ¿Está libre el slug? `ofr` solo al cambiar el de una oferta.
 * Responde { av: true } o { av: false, cd, own? }.
 */
export const checkSlug = ({ sg, ofr } = {}) => post('checkSlug', compact({ sg, ofr }));

/**
 * Pone un programa a la venta. Sin tipo: lo calcula cms-api del programa
 * (contrato, 4.3). La descripción solo en oposiciones: en leyes, sin `ds`.
 * Responde { ofr, sg, fp }.
 */
export const createOffer = ({ oid, nm, sg, ds, fea = false } = {}) => {
  const params = { oid, nm, sg, fea: !!fea };
  if (ds !== undefined) params.ds = ds;
  return post('createOffer', params);
};

/** Quita la oferta de la venta. Responde { ofr, st: 'off' }. */
export const disableOffer = ({ ofr } = {}) => post('disableOffer', { ofr });

/** Vuelve a poner la oferta a la venta. Responde { ofr, st: 'sale' }. */
export const enableOffer = ({ ofr } = {}) => post('enableOffer', { ofr });

/** Una oferta, para «Editar oferta» (también las quitadas). Responde { ofr, nm, ty, sg, ds }. */
export const getOffer = ({ ofr } = {}) => post('getOffer', { ofr });

/**
 * Cambia la URL, la descripción o las dos (contrato, 4.5). Solo se manda lo que
 * cambia: sin `sg` ni `ds`, no se toca nada. Responde { ofr, sg, ds, old }.
 */
export const updateOffer = ({ ofr, sg, ds } = {}) => {
  const params = { ofr };
  if (sg !== undefined) params.sg = sg;
  if (ds !== undefined) params.ds = ds;
  return post('updateOffer', params);
};

/** Destacadas de una web. Responde { ty, max, items, cand }. */
export const getFeatured = ({ ty } = {}) => post('getFeatured', { ty });

/** Guarda la lista completa y en orden de destacadas. Responde como getFeatured. */
export const saveFeatured = ({ ty, ofrs = [] } = {}) => post('saveFeatured', { ty, ofrs });

/** Historial de ecommerce de un programa. Responde { items }. */
export const getHistory = ({ oid } = {}) => post('getHistory', { oid });
